import { renderGrid, renderXAxisLabels, renderYaxisNumerical } from '../../zoompan-canvas/renderAxes';
import { getBoundData, TpChartConfigParams, TpChartDataBinding } from '../chartDataBindings';
import {
  createLogicalRange as createLogicalRange,
  RenderMinDiffAbs,
  RenderMaxDiffAbs,
  createRenderRange as createRenderRange,
  TpAreaRenderContext,
  TpChartAreaDefinition,
} from '../../zoompan-canvas/zoomPanCanvasHelpers';
import { TpDataContext, TpDataTable, VALTYPES } from '../../../../data/interfaces';
import { interpolateColors } from '../../helpers/colors';
import { getStandardDataColor } from '../../../themes/themes';
import { renderLegendItems, renderYLabel, TpLegendItem } from '../../helpers/renderLegend';
import { TpChartManifest } from '../interfaces';
import { createColorMappingInfo, getMappedColorLegendInfo } from '../../helpers/colorMappingInfo';
import { chartDims, convertColumnDataToDisplayString, generateChartTitle, isSelectedItem } from '../../helpers/misc';
import { getLabelSpaceSize } from '../../helpers/configParams';
import { currentTheme } from '../../../themes/themes';
import { getUserError } from '../../../../components/miscUtils';
import { TpCategoryInfo, sortCategories } from '../../helpers/categoricalSorters';
import { TpDashboardGlobalChartConfig } from '../../dashboard/interfaces';

export interface TpBarGraphCatInfo extends TpCategoryInfo {
  colorCategoriesCount: number[];
  colorCategoriesCountSelected: number[];
  positionIndex: number;
}

export function createDefinition(
  chartTypeManifest: TpChartManifest,
  dataContext: TpDataContext,
  dataBinding: TpChartDataBinding,
  config: TpChartConfigParams,
  widgetGlobalConfig: TpDashboardGlobalChartConfig,
) {
  const boundData = getBoundData(chartTypeManifest, dataContext, dataBinding);
  const dataTable: TpDataTable = boundData.dataTable;
  const dataValue = boundData.bindings.value.data;
  const dataColor = boundData.bindings.color?.data;
  const dataItemId = boundData.bindings.itemId.data;
  const nameX = boundData.bindings.value.columnInfo.name;
  const nameY = 'Count';
  const title = generateChartTitle(dataContext, dataBinding, config, nameX);
  const valueColumnInfo = boundData.bindings.value.columnInfo;

  const itemCount = dataValue.length;

  const selectedItemsMap = boundData.canonicalTypeInfo.selectedItemsMap;

  const globalColorMappingConfig = widgetGlobalConfig.colorMapping;
  const colorMappingInfo = createColorMappingInfo(config, boundData, 'color', 1, globalColorMappingConfig);
  const hasColors = colorMappingInfo.hasCategoricalColors;
  const colorCategoriesCount = colorMappingInfo.categoriesCount!;

  // Only show percentages if there are colors
  // (it is possible to unbind the color aspect without touching the yAxisType config)
  const showPercentages = hasColors && config.yAxisType == 'percentage';

  const zeroCats = [];
  for (let i = 0; i < colorCategoriesCount; i++) zeroCats.push(0);

  const multiValuedCats = boundData.bindings.value.columnInfo.valueType == VALTYPES.MULTICAT;

  const categoryMap: { [cat: string]: TpBarGraphCatInfo } = {};
  const categories: TpBarGraphCatInfo[] = [];
  for (let i = 0; i < itemCount; i++) {
    const catValueList = !multiValuedCats ? [dataValue[i]] : dataValue[i];
    for (const catValue of catValueList) {
      if (!categoryMap[catValue]) {
        const cat: TpBarGraphCatInfo = {
          value: catValue,
          displayString: convertColumnDataToDisplayString(valueColumnInfo, catValue),
          count: 0,
          countSelected: 0,
          colorCategoriesCount: [...zeroCats],
          colorCategoriesCountSelected: [...zeroCats],
          positionIndex: 0,
        };
        categories.push(cat);
        categoryMap[catValue] = cat;
      }
      categoryMap[catValue].count++;
      if (isSelectedItem(selectedItemsMap, dataItemId[i])) categoryMap[catValue].countSelected++;
      if (hasColors) {
        const colorCatIdx = colorMappingInfo.colorCat2Index[dataColor[i]];
        categoryMap[catValue].colorCategoriesCount[colorCatIdx]++;
        if (isSelectedItem(selectedItemsMap, dataItemId[i]))
          categoryMap[catValue].colorCategoriesCountSelected[colorCatIdx]++;
      }
    }
  }

  if (categories.length > 50000) throw getUserError('Too many categories');

  sortCategories(categories, config.sortBy);

  let posIndex = 0;
  for (const cat of categories) {
    cat.positionIndex = posIndex;
    posIndex++;
  }

  let maxCount = 1;
  for (const cat of categories) if (maxCount < cat.count) maxCount = cat.count;
  if (config.showTotalCount) maxCount = itemCount;
  if (showPercentages) maxCount = 100;
  const catCount = categories.length;
  const colInfoCats = boundData.bindings.value.columnInfo;
  const displayStrings = categories.map((cat) => convertColumnDataToDisplayString(colInfoCats, cat.value));

  function renderColumnLabels(renderContext: TpAreaRenderContext) {
    renderXAxisLabels(renderContext, displayStrings);
  }

  function renderCategories(renderContext: TpAreaRenderContext) {
    const { mapx, mapy, pixelConv } = renderContext;
    const ctx = renderContext.canvasContext;

    renderGrid(renderContext, false, true);

    const unSelectedFill = interpolateColors(
      getStandardDataColor(),
      currentTheme.colors.background,
      0.3,
    ).toCanvasString();
    const selectedFill = getStandardDataColor().toCanvasString();
    ctx.lineWidth = renderContext.pixelRatio * 1;

    let catIdx = 0;
    for (const cat of categories) {
      let scale = 1;
      if (showPercentages) scale = 100 / cat.count;
      const x1 = mapx((catIdx + 0.1) / catCount);
      const x2 = mapx((catIdx + 0.9) / catCount);

      ctx.fillStyle = unSelectedFill;
      ctx.beginPath();
      ctx.rect(x1, mapy(0), x2 - x1, mapy(cat.count * scale) - mapy(0));
      ctx.fill();

      if (!hasColors) {
        ctx.fillStyle = selectedFill;
        ctx.beginPath();
        ctx.rect(x1, mapy(0), x2 - x1, mapy(cat.countSelected * scale) - mapy(0));
        ctx.fill();
      } else {
        let countAccum = 0;
        for (
          let colorCatIdx = 0;
          colorCatIdx < Math.min(colorCategoriesCount, colorMappingInfo.colorValueStrings.length);
          colorCatIdx++
        ) {
          const colorCatCount = cat.colorCategoriesCount[colorCatIdx];
          const colorCatCountSelected = cat.colorCategoriesCountSelected[colorCatIdx];
          ctx.fillStyle = colorMappingInfo.colorValueStrings[colorCatIdx];
          ctx.beginPath();
          ctx.rect(
            x1,
            mapy(countAccum * scale),
            x2 - x1,
            mapy((countAccum + colorCatCountSelected) * scale) - mapy(countAccum * scale),
          );
          ctx.fill();
          countAccum += colorCatCount;
        }
      }

      catIdx++;
    }
  }

  function renderLegend(renderContext: TpAreaRenderContext) {
    const legendItems: TpLegendItem[] = [];
    if (colorMappingInfo.hasColors) legendItems.push(getMappedColorLegendInfo(colorMappingInfo));
    renderLegendItems(renderContext, legendItems);
  }

  const hasLegend = hasColors;
  const legendBottomSize = hasLegend ? chartDims.legendHeight : 0;

  const padding = chartDims.padding;

  const xAxisHeight = getLabelSpaceSize(config.labelSpace);

  const zpLogicalRangeX = createLogicalRange(0, 1); //-0.5 / catCount, 1 + 0.5 / catCount);
  const zpLogicalRangeY = createLogicalRange(-0.01 * maxCount, maxCount * 1.05);
  const zpRenderRangeX = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.yAxisWidth),
    RenderMaxDiffAbs(padding + 0),
  );
  const zpRenderRangeY = createRenderRange(
    RenderMinDiffAbs(padding + chartDims.headerHeight),
    RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
  );

  const areaPoints: TpChartAreaDefinition = {
    id: 'bars',
    logicalRangeX: zpLogicalRangeX,
    logicalRangeY: zpLogicalRangeY,
    renderRangeX: zpRenderRangeX,
    renderRangeY: zpRenderRangeY,
    zoomPanX: true,
    zoomPanY: true,
    // canAreaSelect: false,
    render: renderCategories,
  };

  const chartAreas: TpChartAreaDefinition[] = [
    areaPoints,
    {
      id: 'xaxis',
      logicalRangeX: zpLogicalRangeX,
      logicalRangeY: createLogicalRange(0, 1),
      renderRangeX: zpRenderRangeX,
      renderRangeY: createRenderRange(
        RenderMaxDiffAbs(padding + xAxisHeight + legendBottomSize),
        RenderMaxDiffAbs(padding + legendBottomSize),
      ),
      zoomPanX: true,
      render: renderColumnLabels,
    },
    {
      id: 'yaxis',
      logicalRangeX: createLogicalRange(0, 1),
      logicalRangeY: zpLogicalRangeY,
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMinDiffAbs(padding + chartDims.yAxisWidth)), // offset of Y=4 to make axes overlap
      renderRangeY: zpRenderRangeY,
      zoomPanY: true,
      render: renderYaxisNumerical,
    },
    {
      id: 'ylabel',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding + 0), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(
        RenderMinDiffAbs(padding + 0),
        RenderMinDiffAbs(padding + chartDims.headerHeight),
      ),
      render: (renderContext: TpAreaRenderContext) => renderYLabel(renderContext, nameY, title),
    },
  ];

  if (hasLegend) {
    chartAreas.push({
      id: 'legend',
      renderRangeX: createRenderRange(RenderMinDiffAbs(padding), RenderMaxDiffAbs(padding + 0)),
      renderRangeY: createRenderRange(RenderMaxDiffAbs(padding + legendBottomSize), RenderMaxDiffAbs(padding)),
      render: renderLegend,
    });
  }

  const definition = {
    zpLogicalRangeX,
    zpLogicalRangeY,
    zpRenderRangeX,
    zpRenderRangeY,
    areas: chartAreas,
  };

  return {
    definition,
    areaPoints,
    dataTable,
    dataValue,
    valueColumnInfo,
    nameX,
    nameY,
    itemCount,
    dataItemId,
    categories,
    categoryMap,
    showPercentages,
    title,
  };
}
